import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import API_CONFIG from '../../config/apiConfig';
import { apiClient } from '../../config/apiConfig';
import socketService from '../../services/socketService';

const { endpoints } = API_CONFIG;

const AuthContext = createContext(null);

// Session check retry: 2s, 4s, 8s ... capped at 60s, so a struggling server sees at most one verify a minute
export const VERIFY_RETRY_BASE_MS = 2000;
export const VERIFY_RETRY_MAX_MS = 60000;
const VERIFY_TIMEOUT_MS = 15000;
const RECONNECTING_TEXT = 'Reconnecting to the server';

// Retry-After is either seconds or an HTTP date
export const parseRetryAfterMs = (value) => {
  if (value === undefined || value === null || value === '') return null;
  const seconds = Number(value);
  if (Number.isFinite(seconds)) return Math.max(0, seconds * 1000);
  const date = Date.parse(value);
  return Number.isNaN(date) ? null : Math.max(0, date - Date.now());
};

const readCachedUser = () => {
  try {
    const raw = localStorage.getItem('user');
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

const cacheUser = (user) => {
  try {
    localStorage.setItem('user', JSON.stringify(user));
  } catch {
    // Storage full or blocked: the session still works, it just can't survive an outage on reload
  }
};

export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reconnecting, setReconnecting] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const controller = new AbortController();
    let retryTimer = null;
    let attempt = 0;

    if (!localStorage.getItem('token')) {
      setLoading(false);
      setIsAuthenticated(false);
      return undefined;
    }

    const settle = () => {
      setReconnecting(false);
      setLoading(false);
    };

    // A 429, 5xx, timeout or network error says nothing about the token: keep the
    // session (cached user if there is one) and try again later
    const keepSessionAndRetry = (error) => {
      const status = error?.response?.status;
      console.warn('[AuthContext] Session check failed, retrying:', { status, message: error?.message });

      const cachedUser = readCachedUser();
      if (cachedUser) {
        setUser(current => current || cachedUser);
        setIsAuthenticated(true);
        setLoading(false);
      }
      setReconnecting(true);

      const backoffMs = Math.min(VERIFY_RETRY_BASE_MS * 2 ** attempt, VERIFY_RETRY_MAX_MS);
      const retryAfterMs = status === 429 ? parseRetryAfterMs(error.response.headers?.['retry-after']) : null;
      attempt += 1;
      retryTimer = setTimeout(checkAuth, Math.max(backoffMs, retryAfterMs || 0));
    };

    const checkAuth = async () => {
      try {
        // Verify token with backend
        const response = await apiClient.get(endpoints.auth.verify, {
          signal: controller.signal,
          timeout: VERIFY_TIMEOUT_MS
        });
        if (response?.data?.valid) {
          setIsAuthenticated(true);
          setUser(response.data.user);
          cacheUser(response.data.user);
          settle();
        } else if (response?.data?.valid === false) {
          handleLogout();
          settle();
        } else {
          // A 2xx without the verify payload (e.g. a proxy page) is not a verdict on the token
          keepSessionAndRetry(new Error('Unexpected session check response'));
        }
      } catch (error) {
        if (controller.signal.aborted) return;
        // server/middleware/auth.js answers every missing, invalid, expired or revoked
        // token with 401, so that is the only status that ends the session
        if (error?.response?.status === 401) {
          handleLogout();
          settle();
          return;
        }
        keepSessionAndRetry(error);
      }
    };

    checkAuth();
    return () => {
      controller.abort();
      clearTimeout(retryTimer);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setIsAuthenticated(false);
    setUser(null);

    // Disconnect socket on logout to prevent stale connections
    socketService.disconnect();

    if (location.pathname !== '/login') {
      // Remember where the user was so login returns there (e.g. #/connect?request=...)
      navigate('/login', { state: { from: location } });
    }
  };

  const login = async (username, password) => {
    try {
      console.log('Attempting login:', { username });
      
      const response = await apiClient.post(endpoints.auth.login, {
        username,
        password
      });

      console.log('Login response:', response);

      if (!response || !response.data) {
        throw new Error('Invalid response from server');
      }

      const { token, user } = response.data;

      if (!token || !user) {
        throw new Error('Missing token or user data');
      }

      // Store token and update state
      localStorage.setItem('token', token);
      cacheUser(user);
      setIsAuthenticated(true);
      setUser(user);

      // Reconnect socket with fresh auth token
      // This ensures the socket uses the new token for authentication
      socketService.reconnectWithNewToken();

      // Redirect if there's a return URL (query param, or the page ProtectedRoute sent us from)
      const returnUrl = new URLSearchParams(location.search).get('returnUrl');
      const from = location.state?.from;
      const fromUrl = from?.pathname && from.pathname !== '/login' ? `${from.pathname}${from.search || ''}` : null;
      navigate(returnUrl || fromUrl || '/');

      return response.data;
    } catch (error) {
      console.error('Login error:', error);
      handleLogout();
      throw {
        message: error.response?.data?.message || error.message,
        code: 'AUTH_ERROR',
        status: error.response?.status || 500,
        data: error.response?.data
      };
    }
  };

  if (loading) {
    return <div role="status">{reconnecting ? RECONNECTING_TEXT : 'Loading...'}</div>;
  }

  const hasPermission = (perm) => {
    if (!user) return false;
    if (user.role === 'admin') return true;
    // If role is missing (old backend without permissions support), fail-open
    // — backend is the real enforcement layer
    if (!user.role) return true;
    const perms = Array.isArray(perm) ? perm : [perm];
    return perms.some(p => (user.permissions || []).includes(p));
  };

  const value = {
    isAuthenticated,
    user,
    login,
    logout: handleLogout,
    loading,
    reconnecting,
    hasPermission
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
      {reconnecting && (
        <div
          role="status"
          aria-live="polite"
          className="fixed top-2 left-1/2 -translate-x-1/2 z-50 bg-surface-3 text-text-secondary text-xs px-3 py-1 pointer-events-none"
        >
          {RECONNECTING_TEXT}
        </div>
      )}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;